import type * as ents from '@attain-apps/entities'
import * as store from '@attain-apps/fe/web/main/lib/store'
import { PartialId, Word } from '@attain-apps/string-builder'

import type * as models from '../../models'
import type { Entity } from '../../types'

import { State } from './state'

/**
 * Core actions for this plugin
 */
// NOTE: intentionally missing return type so that type can be inferred
export function getActionsConfig() {
  return {
    /**
     * Forget the info entries held for a message that is being produced again
     */
    dropInfoEntries: store.actions.standalone({
      data: store.actions.Data.asIs<{ readonly messageId: Entity['id'] }>(),
      reducer: ({ state, data: { messageId } }) =>
        State.withoutInfoEntries(state, messageId),
    }),
    /**
     * Forget sent messages that a read of their conversation now lists
     */
    dropSent: store.actions.standalone({
      data: store.actions.Data.asIs<{
        readonly ids: readonly Entity['id'][]
      }>(),
      reducer: ({ state, data: { ids } }) => State.withoutSent(state, ids),
    }),
    /**
     * Remember a message sent from here until a read of its conversation lists
     * it
     */
    putSent: store.actions.standalone({
      data: store.actions.Data.asIs<{ readonly message: Entity }>(),
      reducer: ({ state, data: { message } }) =>
        State.withSent(state, message),
    }),
    /**
     * Take in info entries published for the conversation's messages
     */
    putInfoEntries: store.actions.standalone({
      data: store.actions.Data.asIs<{
        readonly entries: readonly ents.core.agent.Info.Stored[]
      }>(),
      reducer: ({ state, data: { entries } }) =>
        State.withInfoEntries(state, entries),
    }),
    /**
     * Retry sending a failed message
     */
    retryMessage: store.actions.request({
      input: store.actions.Data.asIs<models.RetryMessageParams>(),
      output: store.actions.Data.NONE,
    }),
    /**
     * Send a message to the agent with file uploads
     */
    sendMessage: store.actions.request({
      input: (
        input: Partial.Semi<
          models.SendMessageParams,
          'messageId' | 'conversationId' | 'agentId'
        >,
      ) => ({
        ...input,
        conversationId: input.conversationId ?? PartialId.from(Word.generate()),
        messageId: input.messageId ?? PartialId.from(Word.generate()),
      }),
      output: store.actions.Data.NONE,
    }),
  } as const satisfies store.actions.Config.Constraint<State>
}
